import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useAuthStore } from '../stores/authStore'
import { API_BASE } from '../lib/api'
import { ConfirmDialog } from '../components/ConfirmDialog'
import { InlineError } from '../components/InlineError'
import '../styles/ui.css'

type PermissionLabel = { flag: number; label: string }

type Classification = {
    id: string
    name: string
    description: string | null
    permissionFlags: number
    isSeeded: boolean
    sortOrder: number
    permissions: string[]
}

type ClassificationsResponse = {
    classifications: Classification[]
    allPermissions: PermissionLabel[]
}

const EMPTY_FORM = { name: '', description: '', permissionFlags: 0 }

export default function AdminClassificationsPage() {
    const token = useAuthStore(s => s.accessToken)
    const qc = useQueryClient()
    const [modal, setModal] = useState<{ mode: 'create' | 'edit'; item?: Classification } | null>(null)
    const [form, setForm] = useState(EMPTY_FORM)
    const [error, setError] = useState<string | null>(null)
    const [deleteError, setDeleteError] = useState<string | null>(null)
    const [confirmDelete, setConfirmDelete] = useState<Classification | null>(null)

    const authHeaders = { Authorization: `Bearer ${token}` }

    const { data, isLoading } = useQuery<ClassificationsResponse>({
        queryKey: ['classifications'],
        queryFn: async () => {
            const res = await fetch(API_BASE + '/api/admin/classifications', { headers: authHeaders })
            return res.json()
        }
    })

    const openCreate = () => {
        setForm(EMPTY_FORM)
        setError(null)
        setModal({ mode: 'create' })
    }

    const openEdit = (c: Classification) => {
        setForm({ name: c.name, description: c.description ?? '', permissionFlags: c.permissionFlags })
        setError(null)
        setModal({ mode: 'edit', item: c })
    }

    const toggleFlag = (flag: number) => {
        setForm(f => ({ ...f, permissionFlags: f.permissionFlags ^ flag }))
    }

    const hasFlag = (flag: number) => (form.permissionFlags & flag) !== 0

    const save = useMutation({
        mutationFn: async () => {
            if (!form.name.trim()) throw new Error('Name is required.')
            if (form.permissionFlags === 0) throw new Error('Select at least one permission.')

            const url = modal?.mode === 'edit'
                ? `/api/admin/classifications/${modal.item!.id}`
                : '/api/admin/classifications'
            const method = modal?.mode === 'edit' ? 'PUT' : 'POST'

            const res = await fetch(API_BASE + url, {
                method,
                headers: { ...authHeaders, 'Content-Type': 'application/json' },
                body: JSON.stringify(form)
            })
            const body = await res.json()
            if (!res.ok) throw new Error(body.error ?? 'Failed to save.')
            return body
        },
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ['classifications'] })
            setModal(null)
            setError(null)
        },
        onError: (e: Error) => setError(e.message)
    })

    const del = useMutation({
        mutationFn: async (id: string) => {
            const res = await fetch(API_BASE + `/api/admin/classifications/${id}`, {
                method: 'DELETE', headers: authHeaders
            })
            const body = await res.json()
            if (!res.ok) throw new Error(body.error ?? 'Failed to delete.')
        },
        onSuccess: () => qc.invalidateQueries({ queryKey: ['classifications'] }),
        onError: (e: Error) => setDeleteError(e.message)
    })

    const allPermissions = data?.allPermissions ?? []

    return (
        <>
            <div className="page-header">
                <h1>Staff Classifications</h1>
                <p>Define permission sets for different types of store staff.</p>
            </div>

            {deleteError && (
                <InlineError message={deleteError} onDismiss={() => setDeleteError(null)} style={{ marginBottom: 'var(--space-4)' }} />
            )}

            {/* Classification Cards */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)', marginBottom: 'var(--space-5)' }}>
                {isLoading ? (
                    <div className="empty-state"><p>Loading…</p></div>
                ) : data?.classifications.map(c => (
                    <div key={c.id} className="data-table-wrapper" style={{ padding: 'var(--space-5)' }}>
                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--space-4)' }}>
                            {/* Info */}
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', marginBottom: 'var(--space-2)' }}>
                                    <span style={{ fontSize: 'var(--text-base)', fontWeight: 700 }}>{c.name}</span>
                                    {c.isSeeded && (
                                        <span className="badge badge-unassigned" title="Built-in — cannot be deleted">🔒 Built-in</span>
                                    )}
                                </div>
                                {c.description && (
                                    <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)', marginBottom: 'var(--space-3)' }}>
                                        {c.description}
                                    </p>
                                )}
                                {/* Permission chips */}
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-2)' }}>
                                    {allPermissions.map(p => {
                                        const granted = (c.permissionFlags & p.flag) !== 0
                                        return (
                                            <span
                                                key={p.flag}
                                                className={`badge ${granted ? 'badge-approved' : 'badge-inactive'}`}
                                                title={granted ? 'Granted' : 'Not granted'}
                                            >
                                                {granted ? '✓' : '✕'} {p.label}
                                            </span>
                                        )
                                    })}
                                </div>
                            </div>

                            {/* Actions */}
                            <div className="table-actions" style={{ flexShrink: 0 }}>
                                <button className="btn-icon" title="Edit" onClick={() => openEdit(c)}>✏</button>
                                {!c.isSeeded && (
                                    <button
                                        className="btn-icon danger"
                                        title="Delete"
                                        onClick={() => setConfirmDelete(c)}
                                    >
                                        🗑
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            <button className="btn btn-primary" onClick={openCreate}>+ New Classification</button>

            {/* Create / Edit Modal */}
            {modal && (
                <div className="modal-backdrop">
                    <div className="modal" style={{ maxWidth: 540 }}>
                        <div className="modal-header">
                            <h2>{modal.mode === 'create' ? 'New Classification' : `Edit: ${modal.item?.name}`}</h2>
                            <button className="modal-close" onClick={() => setModal(null)}>✕</button>
                        </div>

                        <div className="modal-body">
                            <div className="form-field">
                                <label className="form-label">Name *</label>
                                <input
                                    className="form-input"
                                    placeholder="e.g. Seasonal, Lead Staff…"
                                    value={form.name}
                                    onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                                />
                            </div>

                            <div className="form-field">
                                <label className="form-label">Description</label>
                                <textarea
                                    className="form-textarea"
                                    placeholder="Briefly describe when this classification applies…"
                                    value={form.description}
                                    onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                                    style={{ minHeight: 60 }}
                                />
                            </div>

                            <div className="form-field">
                                <label className="form-label">Permissions *</label>
                                <div className="permission-grid">
                                    {allPermissions.map(p => (
                                        <label key={p.flag} className="permission-toggle">
                                            <input
                                                type="checkbox"
                                                checked={hasFlag(p.flag)}
                                                onChange={() => toggleFlag(p.flag)}
                                            />
                                            <span className="permission-toggle-label">{p.label}</span>
                                        </label>
                                    ))}
                                </div>
                            </div>

                            {error && (
                                <p style={{ color: 'var(--color-error)', fontSize: 'var(--text-sm)', marginTop: 'var(--space-2)' }}>
                                    {error}
                                </p>
                            )}
                        </div>

                        <div className="modal-footer">
                            <button className="btn btn-secondary" onClick={() => setModal(null)}>Cancel</button>
                            <button
                                className="btn btn-primary"
                                disabled={save.isPending}
                                onClick={() => save.mutate()}
                            >
                                {save.isPending ? 'Saving…' : 'Save Classification'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {confirmDelete && (
                <ConfirmDialog
                    title="Delete classification?"
                    message={`Delete "${confirmDelete.name}"? Staff using it will lose their permissions.`}
                    confirmLabel="Delete"
                    danger
                    onCancel={() => setConfirmDelete(null)}
                    onConfirm={() => { setDeleteError(null); del.mutate(confirmDelete.id); setConfirmDelete(null) }}
                />
            )}
        </>
    )
}
